'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, Mail, Lock, User, Phone, AlertCircle, ArrowRight, CheckCircle } from 'lucide-react';

const ROLES = [
  { value: 'user', label: 'Alıcı / Kiracı', desc: 'Ev arıyorum', icon: '🏠' },
  { value: 'seller', label: 'İlan Sahibi', desc: 'Mülkümü satmak / kiralamak istiyorum', icon: '🔑' },
  { value: 'agent', label: 'Emlakçı / Broker', desc: 'Profesyonel emlak danışmanıyım', icon: '🏢' },
];

export default function KayitPage() {
  const router = useRouter();
  const { signUp } = useAuth();

  const [step, setStep] = useState<1 | 2>(1);
  const [role, setRole] = useState('user');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Şifreler eşleşmiyor.');
      return;
    }
    if (password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      return;
    }

    setLoading(true);
    try {
      await signUp(email, password, {
        full_name: fullName,
        role,
        phone,
      });
      // Redirect based on role
      if (role === 'agent') {
        router.push('/agent-portal');
      } else {
        router.push('/');
      }
      router.refresh();
    } catch (err: any) {
      if (err?.message?.includes('already registered')) {
        setError('Bu e-posta adresi zaten kayıtlı. Giriş yapmayı deneyin.');
      } else {
        setError(err?.message || 'Kayıt olunamadı. Lütfen tekrar deneyin.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link href="/" className="inline-flex items-center gap-2.5 mb-4">
            <AppLogo size={40} />
            <span className="font-extrabold text-2xl tracking-tight text-foreground">Evora</span>
          </Link>
          <h1 className="text-2xl font-extrabold text-foreground">Hesap Oluşturun</h1>
          <p className="text-muted-foreground mt-1 text-sm">Türkiye'nin akıllı emlak platformuna katılın</p>
        </div>

        <div className="card-elevated rounded-2xl p-6">
          {/* Step 1: Role Selection */}
          {step === 1 && (
            <div>
              <h2 className="text-base font-bold text-foreground mb-4">Hesap türünüzü seçin</h2>
              <div className="space-y-3 mb-6">
                {ROLES.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setRole(r.value)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                      role === r.value
                        ? 'border-primary bg-primary-light' :'border-border hover:border-primary/40 hover:bg-muted/50'
                    }`}
                  >
                    <span className="text-2xl">{r.icon}</span>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-foreground">{r.label}</p>
                      <p className="text-xs text-muted-foreground">{r.desc}</p>
                    </div>
                    {role === r.value && (
                      <CheckCircle size={18} className="text-primary flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setStep(2)}
                className="w-full btn-primary py-3 text-sm font-bold flex items-center justify-center gap-2"
              >
                Devam Et
                <ArrowRight size={16} />
              </button>
            </div>
          )}

          {/* Step 2: Account Details */}
          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  ← Geri
                </button>
                <span className="text-sm font-semibold text-foreground">
                  {ROLES.find((r) => r.value === role)?.label} hesabı
                </span>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-danger/10 text-danger text-sm font-medium">
                  <AlertCircle size={16} className="flex-shrink-0" />
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Ad Soyad</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Adınız Soyadınız"
                    required
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-border bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">E-posta</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ornek@evora.com"
                    required
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-border bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Telefon <span className="text-muted-foreground font-normal">(isteğe bağlı)</span></label>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+90 5XX XXX XX XX"
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-border bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Şifre</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="En az 6 karakter"
                    required
                    className="w-full pl-9 pr-10 py-2.5 rounded-xl border border-border bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Şifre Tekrar</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Şifrenizi tekrar girin"
                    required
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-border bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    Hesap Oluştur
                    <ArrowRight size={16} />
                  </>
                )}
              </button>

              <p className="text-xs text-muted-foreground text-center">
                Kayıt olarak{' '}
                <Link href="/" className="text-primary hover:underline">Kullanım Koşulları</Link>
                {' '}ve{' '}
                <Link href="/" className="text-primary hover:underline">Gizlilik Politikası</Link>
                'nı kabul etmiş olursunuz.
              </p>
            </form>
          )}

          <div className="mt-4 text-center text-sm text-muted-foreground">
            Zaten hesabınız var mı?{' '}
            <Link href="/giris" className="text-primary font-semibold hover:underline">
              Giriş Yapın
            </Link>
          </div>
        </div>

        <div className="text-center mt-4">
          <Link href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← Ana Sayfaya Dön
          </Link>
        </div>
      </div>
    </div>
  );
}
